import type { MarketingResource } from "creso-core/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "web-design-system";
import { getCORSSafeURL } from "@/utils/url";
import { webDownload } from "creso-core/hooks";
import { useMarketingCreativeContext } from "./marketing-creative-preview";
import { downloadWebStoryImagesAsZip } from "../utils/download-web-story-images";
import type { WebStoryBrandingDetails } from "./web-story-end-card";
import {
  generateWebStoryVideoFromAttachments,
  getWebStoryImageAttachments,
  preloadWebStoryFFmpeg,
} from "../utils/generate-web-story-video";
import { WebStoryPreviewCarousel } from "./web-story-preview-carousel";

function buildWebStoryBrandingDetails(
  context: ReturnType<typeof useMarketingCreativeContext>
): WebStoryBrandingDetails | undefined {
  const distributorName =
    context.branding?.brand_name || context.profile?.name || "";

  if (!distributorName) {
    return undefined;
  }

  const phoneDisplay =
    `${context.contactCallingCodeLabel} ${context.contactNationalNumber}`.trim();

  return {
    distributorName,
    tagline: context.branding?.tag_line,
    arn: context.arn,
    phoneDisplay,
    flagImageUrl: context.contactFlagImageUrl,
    brandLogoUrl:
      typeof context.branding?.brand_logo === "string"
        ? getCORSSafeURL(context.branding.brand_logo)
        : null,
  };
}

type WebStoryPreviewDialogProps = {
  items: MarketingResource[];
  selectedId: number | null;
  onSelectedIdChange: (id: number | null) => void;
};

export function WebStoryPreviewDialog({
  items,
  selectedId,
  onSelectedIdChange,
}: WebStoryPreviewDialogProps) {
  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId]
  );
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const marketingContext = useMarketingCreativeContext();
  const slideBranding = useMemo(
    () => buildWebStoryBrandingDetails(marketingContext),
    [marketingContext]
  );
  const endCardDetails = useMemo(
    () => buildWebStoryBrandingDetails(marketingContext),
    [marketingContext]
  );

  const imageAttachments = useMemo(
    () => (selectedItem ? getWebStoryImageAttachments(selectedItem) : []),
    [selectedItem]
  );
  const canDownloadVideo = imageAttachments.length > 0;

  useEffect(() => {
    if (selectedItem) {
      preloadWebStoryFFmpeg();
    }
  }, [selectedItem]);

  const handleDownloadAllImages = useCallback(async () => {
    if (!selectedItem) {
      return;
    }

    if (imageAttachments.length === 0) {
      toast.error("No images available to download");
      return;
    }

    try {
      setIsDownloadingImages(true);
      await downloadWebStoryImagesAsZip(selectedItem, slideBranding);
      toast.success(
        imageAttachments.length === 1
          ? "Image downloaded as zip"
          : `${imageAttachments.length} images downloaded as zip`
      );
    } catch {
      toast.error("Failed to download images");
    } finally {
      setIsDownloadingImages(false);
    }
  }, [imageAttachments, selectedItem, slideBranding]);

  const handleDownloadVideo = useCallback(async () => {
    if (!selectedItem) {
      return;
    }

    const imageUrls = imageAttachments.map(
      (attachment) => attachment.attachment
    );

    if (imageUrls.length === 0) {
      toast.error("No images available to generate video");
      return;
    }

    const toastId = toast.loading("Generating video…");

    try {
      setIsDownloadingVideo(true);
      const videoBlob = await generateWebStoryVideoFromAttachments(
        imageUrls,
        endCardDetails
      );
      webDownload(videoBlob, `${selectedItem.title}.mp4`);
      toast.success("Video downloaded", { id: toastId });
    } catch {
      toast.error("Failed to generate video", { id: toastId });
    } finally {
      setIsDownloadingVideo(false);
    }
  }, [endCardDetails, imageAttachments, selectedItem]);

  if (!selectedItem) {
    return null;
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onSelectedIdChange(null);
        }
      }}
    >
      <DialogContent className="w-[95vw] max-w-[280px] sm:max-w-[300px] gap-0 rounded-3xl border-0 p-0 overflow-hidden">
        <DialogTitle className="sr-only">
          {selectedItem.title} web story preview
        </DialogTitle>
        <WebStoryPreviewCarousel
          item={selectedItem}
          onDownloadImages={handleDownloadAllImages}
          onDownloadVideo={handleDownloadVideo}
          isDownloadingImages={isDownloadingImages}
          isDownloadingVideo={isDownloadingVideo}
          canDownloadVideo={canDownloadVideo}
        />
      </DialogContent>
    </Dialog>
  );
}
